import {ProjectShort} from "../projects/Project";
import {Nullable} from "../common/Nullable";
import {ProjectStagePhaseShort} from "../projects/ProjectStagePhase";
import {ProjectStageShort} from "../projects/ProjectStage";
import { EmployeeShort} from "../users/Employee";
import {Module} from "../common/Module";
import {isPrivileged} from "../../config/Privileges";
import {AllNullable, Identifier} from "../common/Identifier";
import {Lockable} from "../common/Lockable";
import {WorkingHours} from "./WorkingHours";
import {TimesheetTask} from "./TimesheetTask";
import {SeriesAppointmentId, SeriesAppointmentRequest} from "./SeriesAppointment";
import {Dateable, TimePeriodable} from "../common/TimePeriodable";
import {AuthenticatedUser} from "../users/AuthenticatedUser";
import {TimesheetState} from "../../reducers/timesheet/TimesheetReducer";
import {OptionType} from "../../components/form/SearchableSelect";
import {formatDateTime} from "../../helpers/DateTimeHelpers";
import { Language } from "../../config/LanguageConfig";
import {Typeable} from "../common/Typeable";
import {IdAndValue} from "../common/IdAndValue";
import {TimesheetPhaseTask} from "./TimesheetPhaseTask";

export const TimesheetAreaName = "timesheet";

export interface TimesheetModuleType extends Module {
    isPrivilegedOneOf: ( authUser: Nullable< AuthenticatedUser >, privileges: string[] ) => boolean
}

export const TimesheetModule: TimesheetModuleType = {
    identifier: "timesheet",
    isPrivileged: (authUser, privilege) => isPrivileged(
        authUser,
        TimesheetAreaName,
        TimesheetModule.identifier,
        privilege ),
    isPrivilegedOneOf: ( authUser, privileges ) => {

        for( let i: number = 0, j: number = privileges.length; i < j ; i++ ) {
            if( TimesheetModule.isPrivileged( authUser, privileges[ i ] ) ) {
                return true;
            }
        }

        return false;
    }
};


export const TimesheetPrivileges = {
    ACCESS_ALL_HOURS_REPORTS: "ACCESS_ALL_HOURS_REPORTS",
    ACCESS_OWN_HOURS_REPORTS: "ACCESS_OWN_HOURS_REPORTS",
    DECIDE_JOURNEY: "DECIDE_JOURNEY",
    DECIDE_WEIGHT: "DECIDE_WEIGHT"
};

export const TIMESHEET_PROJECT_SELECT_ROUTE = "timesheet/projects/select"
export function timesheetProjectStageSelectRoute( projectId: Identifier<number> ) {
    return `timesheet/projects/${ projectId.rawValue }/stages/select`;
}
export function timesheetProjectStagePhaseSelectRoute(
    projectId: Identifier<number>,
    stageId: Identifier<number>
) {
    return `timesheet/projects/${ projectId.rawValue }/stages/${ stageId.rawValue }/phase/select`;
}

export interface TimesheetEntryId {
    timesheetEntryId: Identifier<number>
}
export interface OriginalTimesheetIdentifiable {
    originalId: Nullable< Identifier<number> >
}

export interface Timesheet extends TimesheetEntryId,
    Lockable,
    AllNullable< SeriesAppointmentId >,
    TimePeriodable<Dateable, Dateable>,
    Typeable< Identifier< string > >
{
    employee: EmployeeShort
    hoursOfWork: WorkingHours
    task: TimesheetTask,
    description: Nullable<string>
    timeOfEntry: Dateable
    isInternal: boolean
    isWholeDay: boolean
    isDeleted: boolean
    anfahrt: boolean
}

export interface TimesheetEntryProject extends Omit< Timesheet, "task"> {
    project: ProjectShort,
    projectStage: ProjectStageShort
    projectStagePhase: Nullable< ProjectStagePhaseShort >
    projectStagePhaseTask: IdAndValue< Identifier< number >, string>
    billablePercent: number
    task: TimesheetPhaseTask
}


export type AllTimesheetTypes = TimesheetEntryProject | Timesheet
export type AllTimesheetListTypes = Timesheet[] | TimesheetEntryProject[]

export interface AllTimesheetTypesProp {
    entry: AllTimesheetTypes
}
export interface AllTimesheetTypesListProp {
    entries: AllTimesheetListTypes
}

export const TimesheetTypeIdentifiers = {
    internal: "internal",
    project: "project"
} as const;

export function timesheetTypeIdentifier( timesheet: AllTimesheetTypes ) {
    return ( isTimesheetProjectType( timesheet ) )
        ? TimesheetTypeIdentifiers.project
        : TimesheetTypeIdentifiers.internal
}

/**
 * typescript type guard
 * @param obj
 */
export function isTimesheetProjectType( obj: any ) : obj is TimesheetEntryProject {
    return !!(obj as TimesheetEntryProject).project;
}

export interface TimesheetEntryRequestProp {
    entry: TimesheetRequest
}


export interface TimesheetRequestProjectTaskPath {
    projectsId: number
    projectStageId: number
    projectPhaseId: number
    phaseTaskId: number
}

export interface TimesheetRequestData {
    anfahrt: boolean
    description: Nullable<string>
    billablePercent: number
}

/**
 * Represents a request to create or update timesheet entries associated with a user and project.
 * This interface supports both individual and series-based timesheet operations.
 *
 * The TimesheetRequest encapsulates all the necessary data to perform updates or create new timesheet entries,
 * including handling series operations, task assignments, and scheduling details.
 */
export interface TimesheetRequest {
    timesheetEntryId: number
    otherTimesheetIds: number[]
    updateWholeSeries: boolean
    userId:number
    projectPathId: TimesheetRequestProjectTaskPath
    entryData: TimesheetRequestData
    workPeriod: TimePeriodable<Date, Date>
    taskId:number
    seriesId: number
    isInternal: boolean
    isSeries: boolean
    seriesAppointment: Nullable<SeriesAppointmentRequest>
    isWholeDay: boolean
    type: string
}

/**
 * Represents a request to restore or delete timesheet entries.
 *
 * This type is used to specify the details required for restoring or deleting
 * one or more timesheet entries, either individually or as part of a series.
 * It supports operations on a single timesheet entry or across an entire series
 * of related entries.
 *
 * @property timesheetEntryId The unique identifier of the specific timesheet entry
 * that is targeted for restoration or deletion. This is required for identifying
 * the primary entry to act upon.
 *
 * @property otherTimesheetIds An array of additional timesheet entry IDs that are
 * part of the same operation, typically used when dealing with multiple related
 * entries that require simultaneous updates.
 *
 * @property updateWholeSeries A boolean flag indicating whether the operation
 * should apply to all entries in a related series. If set to `true`, the action
 * will affect the entire series of timesheet entries, not just the specified entry.
 *
 * @throws Will throw an error if `timesheetEntryId` is not a valid number or
 * if inconsistent data is provided (e.g., empty `otherTimesheetIds` while
 * `updateWholeSeries` is `true`).
 */
export type TimesheetRestoreDeleteRequest = {
    timesheetEntryId: number
    otherTimesheetIds: number[]
    updateWholeSeries: boolean
}

/**
 * create timesheet request object from internal or project timesheet object
 * @param timesheet
 * @param otherTimesheetIds
 * @param updateWholeSeries
 */
export function toTimesheetRequest(
    timesheet: AllTimesheetTypes,
    otherTimesheetIds: number[] = [],
    updateWholeSeries: boolean = false
): TimesheetRequest {
    let projectData = {
        projectPathId: {
            projectsId: 0,
            projectStageId: 0,
            projectPhaseId: 0,
            phaseTaskId: 0
        }
    };

    if( isTimesheetProjectType( timesheet ) ) {
        projectData = {
            projectPathId: {
                projectsId: timesheet.project.projectId.rawValue,
                projectStageId: timesheet.projectStage.projectStageId.rawValue,
                projectPhaseId: timesheet.projectStagePhase?.phaseId.rawValue ?? 0,
                phaseTaskId: timesheet.task.phaseTaskId.rawValue
            }
        };
    }

    return {
        ...projectData,
        timesheetEntryId: timesheet.timesheetEntryId.rawValue,
        otherTimesheetIds: otherTimesheetIds,
        updateWholeSeries: updateWholeSeries,
        userId: timesheet.employee.userId.rawValue,
        workPeriod: {
            startAt: new Date( timesheet.startAt ),
            finishAt: new Date( timesheet.finishAt ),
        },
        entryData: {
            description: timesheet.description,
            anfahrt: timesheet.anfahrt,
            //@ts-ignore
            billablePercent: timesheet.hasOwnProperty('billablePercent') ? timesheet.billablePercent : 100,
        },
        taskId:timesheet.task.timesheetTaskId.rawValue,
        seriesId: timesheet.seriesAppointmentId?.rawValue ?? 0,
        isInternal: timesheet.isInternal,
        isSeries: ( timesheet.seriesAppointmentId?.rawValue ?? 0 ) > 0,
        seriesAppointment: null,
        isWholeDay: timesheet.isWholeDay,
        type: timesheet.type.rawValue
    };
}


/**
 * convert other timesheet entries belonging to the same series to OptionType
 * this is important for the confirmAction Dialog
 *
 *
 * @param timesheetState
 * @param timesheetId
 */
export function timesheetSeriesToOptionType( timesheetState: TimesheetState, seriesId: number, language: Language ): OptionType[] {
    if( timesheetState.seriesEntries[ seriesId ] && timesheetState.seriesEntries[ seriesId ].entries !== null  ) {
        //@ts-ignore
        return timesheetState.seriesEntries[ seriesId ].entries.map( ts => ({
            value: ts.timesheetEntryId.rawValue.toString(),
            label: formatDateTime( new Date( ts.startAt ), language ),
        }) );
    }

    return [];
}
